import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Zap, 
  ShieldCheck, 
  Clock, 
  ChevronRight, 
  Laptop, 
  Tv, 
  Wrench,
  Globe
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { 
  electronicsServices, 
  itServices, 
  technicians, 
  testimonials 
} from '@/data/index';
import { ServiceCard, TechnicianCard, TestimonialCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { IMAGES } from '@/assets/images';

const springPresets = {
  gentle: { stiffness: 300, damping: 35 },
  smooth: { stiffness: 200, damping: 40 }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Home() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.HERO_REPAIR_1} 
            alt="Electronics Repair" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={fadeInUp}
            transition={springPresets.smooth}
            className="max-w-3xl"
          >
            <Badge className="mb-6 px-4 py-1.5 bg-primary/10 text-primary border-primary/20 backdrop-blur-sm">
              <Zap className="w-3.5 h-3.5 mr-2" />
              Available 24/7 for Emergency Repairs
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight leading-[1.1]">
              Expert Repair & <br />
              <span className="text-primary">IT Support</span> On-Site
            </h1>
            <p className="text-xl text-muted-foreground mb-10 max-w-xl leading-relaxed">
              From Smart TVs and home appliances to professional IT support and network upgrades. 
              Our certified technicians come to your door within 2 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="h-14 px-8 text-lg font-semibold rounded-full">
                <Link to={ROUTE_PATHS.CONTACT}>Book a Technician</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-14 px-8 text-lg font-semibold rounded-full backdrop-blur-sm">
                <Link to={ROUTE_PATHS.SERVICES}>View Services</Link>
              </Button>
            </div>

            <div className="mt-12 flex items-center gap-8 text-sm font-medium text-muted-foreground">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                90-Day Warranty
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                On-Site in 2 Hours
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Bento */}
      <section className="py-24 bg-accent/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card p-8 rounded-3xl border border-border flex flex-col items-start">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Wrench className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">On-Site Repairs</h3>
              <p className="text-muted-foreground">
                No need to carry heavy appliances. Our mobile workshops are fully equipped to handle repairs at your home or office.
              </p>
            </div>
            <div className="bg-card p-8 rounded-3xl border border-border flex flex-col items-start">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">On-Call IT Support</h3>
              <p className="text-muted-foreground">
                Instant remote assistance for software issues, network troubleshooting, and system optimization whenever you need it.
              </p>
            </div>
            <div className="bg-card p-8 rounded-3xl border border-border flex flex-col items-start">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Internet Experts</h3>
              <p className="text-muted-foreground">
                Resolving complex Wi-Fi dead zones and setting up robust mesh networks to ensure seamless connectivity throughout your space.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Electronics Services Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-12">
            <div>
              <Badge variant="outline" className="mb-4 border-primary/20 text-primary">Hardware Specialists</Badge>
              <h2 className="text-4xl font-bold flex items-center gap-3">
                <Tv className="w-8 h-8 text-primary" />
                Electronics Repair
              </h2>
            </div>
            <Button variant="ghost" asChild className="hidden md:flex group">
              <Link to={ROUTE_PATHS.SERVICES}>
                Explore All Electronics <ChevronRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {electronicsServices.map((service) => (
              <motion.div key={service.id} variants={fadeInUp}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* IT Support Banner */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
              <Laptop className="w-10 h-10" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-1">Need Immediate IT Help?</h3>
              <p className="text-primary-foreground/80">Remote diagnostics and on-site tech support available now.</p>
            </div>
          </div>
          <Button variant="secondary" size="lg" className="h-14 px-10 rounded-full font-bold shadow-lg">
            Call (555) 934-2026
          </Button>
        </div>
      </section>

      {/* IT Support Services Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-12">
            <div>
              <Badge variant="outline" className="mb-4 border-primary/20 text-primary">Tech Infrastructure</Badge>
              <h2 className="text-4xl font-bold flex items-center gap-3">
                <Laptop className="w-8 h-8 text-primary" />
                IT & Network Support
              </h2>
            </div>
            <Button variant="ghost" asChild className="hidden md:flex group">
              <Link to={ROUTE_PATHS.SERVICES}>
                Explore IT Solutions <ChevronRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {itServices.map((service) => (
              <motion.div key={service.id} variants={fadeInUp}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technicians Section */}
      <section className="py-24 bg-accent/30">
        <div className="container mx-auto px-4 text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary/20 text-primary">Meet The Pros</Badge>
          <h2 className="text-4xl font-bold mb-6">Our Certified Technicians</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Background-checked, factory-certified, and highly experienced engineers ready to tackle any technical challenge.
          </p>
        </div>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {technicians.map((tech) => (
              <TechnicianCard key={tech.id} technician={tech} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Trust in Every Repair</h2>
            <p className="text-muted-foreground">See what our local customers have to say about our 2026 service standard.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.HERO_REPAIR_2} 
            alt="Repair Workshop" 
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="bg-card border border-border rounded-[2.5rem] p-12 md:p-24 text-center shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                Ready to Get Your Device <br className="hidden md:block" /> Back to Life?
              </h2>
              <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
                Don't wait for the problem to get worse. Book a diagnostic call now and get a free estimate for your repair or upgrade.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Button asChild size="lg" className="h-16 px-12 text-lg font-bold rounded-full">
                  <Link to={ROUTE_PATHS.CONTACT}>Request On-Site Visit</Link>
                </Button>
                <a href="tel:+15559342026" className="flex items-center gap-4 text-2xl font-bold hover:text-primary transition-colors">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  (555) 934-2026
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
